import appSettings from 'wanderu-config';
import {
    i18nMoment,
    i18nMoney,
} from 'wanderu-i18n';

const TITLE_OR_TEXT = /(Title|Text)$/;
const TITLE_OR_DESCRIPTION = /(Title|Description)$/;
const EXCLUDED_PREFIXES = /(metaPage|onTime)$/;
const S3_URL = appSettings.config.s3.staticAssetsUrl;

function remapImageKeyToSrc(images) {
    if (!images) {
        return;
    }

    return images.map((image) => {
        const {
            url,
            ...imageData
        } = image;

        return {
            ...imageData,
            src: `${S3_URL}${url}`,
        };
    });
}

function mapCarrierData(data) {
    if (!data) {
        return;
    }

    return data.map((carrier) => {
        const {
            code,
            name,
            carrierName,
            images = [],
            ...others
        } = carrier;

        return {
            ...others,
            carrierCode: code,
            carrierName: name || carrierName,
            images: remapImageKeyToSrc(images),
        };
    });
}

function mapContent(content, cityToCityContent, carrierContent) {
    if (!content) {
        return;
    }

    return {
        ...content,
        ...cityToCityContent,
        ...carrierContent,
    };
}

function mapQuickviewData(data) {
    if (!data) {
        return;
    }

    const dataKeys = Object.keys(data);

    return dataKeys.reduce((arr, key) => {
        if (data[key]) {
            return [
                ...arr,
                {
                    name: key,
                    type: typeof data[key],
                    value: data[key],
                },
            ];
        }
        return arr;
    }, []);
}

function mapInfoSectionContent(content, match = TITLE_OR_TEXT) {
    if (!content) {
        return;
    }

    const contentList = Object.keys(content);
    const prefixList = contentList.reduce((set, data) => {
        if (data.match(match)) {
            const prefix = data.split(match);
            if (!prefix[0].match(EXCLUDED_PREFIXES)) {
                set.add(prefix[0]);
            }
        }
        return set;
    }, new Set());

    return [...prefixList]
        .map((prefix) => ({
            prefix,
            title: content[`${prefix}Title`],
            text: match === TITLE_OR_TEXT
                ? content[`${prefix}Text`]
                : content[`${prefix}Description`],
            key: match === TITLE_OR_TEXT
                ? `${prefix}Text`
                : `${prefix}Description`,
        }))
        .filter((prefix) => prefix.title && prefix.text);
}

function _getLocalizationData(cheapestTicketData, dailyTripsData) {
    if (!cheapestTicketData || !dailyTripsData) {
        return {};
    }

    const {
        earliestDeparture,
        latestDeparture,
        otherTransModePricing,
    } = dailyTripsData;
    const {
        cheapestPrice,
        savingsPriceUsd,
    } = cheapestTicketData;

    const dailyTripsDescriptionData = {
        minPriceUsd1: otherTransModePricing.price1Usd
            ? i18nMoney(otherTransModePricing.price1Usd).format()
            : undefined,
        minPriceUsd2: otherTransModePricing.price2Usd
            ? i18nMoney(otherTransModePricing.price2Usd).format()
            : undefined,
        minPriceUsdReverseLink: otherTransModePricing.priceReverseUsd
            ? i18nMoney(otherTransModePricing.priceReverseUsd).format()
            : undefined,
    };

    return {
        ...dailyTripsDescriptionData,
        earliestDepartureTime: i18nMoment(earliestDeparture).toShortTime(true),
        latestDepartureTime: i18nMoment(latestDeparture).toShortTime(true),
        price: i18nMoney(cheapestPrice).format(),
        savingsPrice: i18nMoney(savingsPriceUsd).format(),
    };
}

function mapInfoSectionStructuredData(
    content,
    cheapestTicketData,
    dailyTripsData,
    priceHistory,
) {
    if (!content) {
        return;
    }

    const suppressFAQs = {
        priceMonth: !priceHistory?.monthly,
        priceDay: !priceHistory?.daily,
        priceDate: !priceHistory?.daysUntilDepart,
    };

    const localizationData = _getLocalizationData(cheapestTicketData, dailyTripsData);

    const infoContent = [
        ...mapInfoSectionContent(content),
        ...mapInfoSectionContent(content, TITLE_OR_DESCRIPTION),
    ];

    const mainEntity = infoContent.reduce((arr, info) => {
        if (!info.title || !info.text || suppressFAQs[info.prefix]) {
            return arr;
        }

        return [
            ...arr,
            {
                '@type': 'Question',
                name: info.title,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: {
                        localizationData,
                        key: info.key,
                    },
                },
            },
        ];
    }, []);

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity,
    };
}

function mapPriceHistory(priceHistory = {}) {
    const {
        daily,
        monthly,
        daysUntilDepart,
    } = priceHistory;

    if (!Object.keys(priceHistory).length) {
        return;
    }

    const xTickValuesDaily = daily
        && daily.reduce((arr, { x }, index) => index === 0 || index === Math.ceil(daily.length / 2)
            ? [...arr, x]
            : arr, []);

    const xTickValuesMonthly = monthly
        && monthly.reduce((arr, { x }) => [...arr, x], []);

    const xTickValuesDepartDays = daysUntilDepart
        && daysUntilDepart.reduce((arr, { x }) => x % 10 === 0
            ? [...arr, x]
            : arr, []);

    return {
        ...priceHistory,
        xTickValuesDaily,
        xTickValuesMonthly,
        xTickValuesDepartDays,
    };
}

function mapBlogLinksToImages(blogLinks) {
    return blogLinks.map((blogLink) => ({
        src: blogLink.imageUrl,
        alt: blogLink.postTitle,
        author: '',
        title: blogLink.postTitle,
        url: blogLink.postUrl,
    }));
}

function isNonUsCanada(location) {
    return !['US', 'CA'].includes(location?.countryCode);
}

function getImagesForPlace({ type = '', images = {} } = {}) {
    if (!type || !images || typeof images !== 'object' || typeof images[type] !== 'object' || images[type] === null) {
        return [];
    }

    const imageContent = images[type];

    return imageContent[Object.keys(imageContent)[0]];
}

export {
    getImagesForPlace,
    isNonUsCanada,
    mapBlogLinksToImages,
    mapCarrierData,
    mapContent,
    mapInfoSectionContent,
    mapInfoSectionStructuredData,
    mapPriceHistory,
    mapQuickviewData,
    remapImageKeyToSrc,
};
